/**
 * Filter state. **The URL is the store** (PLAN §6) — there is no zustand
 * store here on purpose.
 *
 * Why: every filtered view is then a free permalink, the browser's own
 * history is the undo stack, and there is exactly one source of truth instead
 * of a store that has to be kept in sync with a query string.
 *
 * How it works:
 *  - `useSyncExternalStore` over a module-level snapshot. Reads are O(1) and
 *    the snapshot is referentially stable, so a re-render only happens when
 *    the filters genuinely change.
 *  - Writes update the snapshot and notify subscribers **synchronously** —
 *    clicking a character chip must feel instant — and then write the URL on a
 *    debounce, so dragging a slider produces one history entry's worth of
 *    work instead of forty.
 *  - `replaceState`, not `pushState`: a slider drag should not bury the page
 *    you arrived from under forty history entries. `popstate` is still
 *    handled, so arriving back at a shared link (or Back out of an in-page
 *    navigation) re-reads the filters from the URL.
 *  - The debounced write is flushed before anything that can observe the URL
 *    (Share, page hide), so what you copy is never one keystroke stale.
 */

import { useSyncExternalStore } from "react";
import {
  DEFAULT_FILTERS,
  type FilterState,
  filtersEqual,
  normalizeFilters,
} from "@/lib/filters/types.ts";
import { decodeFilters, filtersToQuery } from "@/lib/filters/urlCodec.ts";

/** Long enough to coalesce a slider drag, short enough that Share is never waiting on it. */
const WRITE_DEBOUNCE_MS = 250;

const listeners = new Set<() => void>();

let snapshot: FilterState = DEFAULT_FILTERS;
/** The `location.search` `snapshot` was decoded from; `null` until first read. */
let snapshotSearch: string | null = null;
let writeTimer: ReturnType<typeof setTimeout> | null = null;
/** The snapshot is ahead of the address bar — a debounced write is owed. */
let pendingWrite = false;

const hasWindow = (): boolean => typeof window !== "undefined";

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Re-decode from the address bar if it has moved out from under us.
 *
 * Skipped while a write is pending: in that window the snapshot is the newer
 * of the two, and re-reading would roll the user's last click back to whatever
 * the URL still says.
 */
function syncFromLocation(): void {
  if (!hasWindow() || pendingWrite) return;
  const search = window.location.search;
  if (snapshotSearch === search) return;
  snapshotSearch = search;
  const next = decodeFilters(search);
  // Hold identity when nothing meaningful changed, so subscribers don't churn.
  if (!filtersEqual(next, snapshot)) snapshot = next;
}

/**
 * Cheap enough to re-check the URL every read (a string compare in the common
 * case), which keeps non-React readers correct even when nothing is currently
 * subscribed to hear `popstate`.
 */
function getSnapshot(): FilterState {
  syncFromLocation();
  return snapshot;
}

/**
 * No address bar to read, so this is just the snapshot as it stands — which
 * is `DEFAULT_FILTERS` unless something set it explicitly. (There is no SSR
 * here; React only needs this to exist.)
 */
const getServerSnapshot = (): FilterState => snapshot;

/** Write the pending state to the address bar right now. */
export function flushFilterUrl(): void {
  if (writeTimer !== null) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
  if (!hasWindow()) return;
  if (!pendingWrite) return;
  pendingWrite = false;
  const { pathname, hash } = window.location;
  const url = `${pathname}${filtersToQuery(snapshot)}${hash}`;
  window.history.replaceState(window.history.state, "", url);
  snapshotSearch = window.location.search;
}

function scheduleWrite(): void {
  if (!hasWindow()) return;
  if (writeTimer !== null) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    writeTimer = null;
    flushFilterUrl();
  }, WRITE_DEBOUNCE_MS);
}

function onPopState(): void {
  // A history navigation wins over anything we had queued for the old URL.
  if (writeTimer !== null) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
  pendingWrite = false;
  snapshotSearch = null;
  syncFromLocation();
  emit();
}

/**
 * Subscribe to filter changes. `useFilters` is the React door; this is the
 * plain one, and it's what installs the `popstate`/`pagehide` listeners — so
 * while nothing is subscribed the store is passive and reads its state lazily
 * off `location` instead.
 */
export function subscribeToFilters(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  if (listeners.size === 1 && hasWindow()) {
    window.addEventListener("popstate", onPopState);
    // Leaving the page with a pending write would lose it — and the URL people
    // copy out of the address bar is a permalink like any other.
    window.addEventListener("pagehide", flushFilterUrl);
  }
  return () => {
    listeners.delete(onStoreChange);
    if (listeners.size === 0 && hasWindow()) {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pagehide", flushFilterUrl);
      flushFilterUrl();
    }
  };
}

/** The current filters. Re-renders only when they actually change. */
export function useFilters(): FilterState {
  return useSyncExternalStore(subscribeToFilters, getSnapshot, getServerSnapshot);
}

/** Read the filters outside React (event handlers, the solver later). */
export function readFilters(): FilterState {
  return getSnapshot();
}

export function setFilters(update: FilterState | ((prev: FilterState) => FilterState)): void {
  const previous = getSnapshot();
  const next = normalizeFilters(typeof update === "function" ? update(previous) : update);
  if (filtersEqual(next, previous)) return;
  snapshot = next;
  pendingWrite = true;
  emit();
  scheduleWrite();
}

export function resetFilters(): void {
  setFilters(DEFAULT_FILTERS);
}

/** The shareable absolute URL for a state — what the Share button copies. */
export function filterPermalink(state: FilterState): string {
  if (!hasWindow()) return filtersToQuery(state);
  const { origin, pathname } = window.location;
  return `${origin}${pathname}${filtersToQuery(state)}`;
}

/** Test seam: drop all module state so a suite can start from a clean URL. */
export function resetFilterStoreForTests(): void {
  if (writeTimer !== null) clearTimeout(writeTimer);
  writeTimer = null;
  pendingWrite = false;
  snapshot = DEFAULT_FILTERS;
  snapshotSearch = null;
}
